import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Loader2, X } from 'lucide-react';
import { formatMoney } from '@/shared/lib/format/money';
import {
  useSearchUsersQuery,
  useTransferChargesMutation,
  useTransferQndtMutation,
} from '../api/useWalletQueries';

type UserHit = {
  _id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  bankDetails?: unknown;
};

type Props = {
  open: boolean;
  onClose: () => void;
  availableBalance?: number;
};

export function QndtTransferModal({ open, onClose, availableBalance }: Props) {
  const [email, setEmail] = useState('');
  const [debouncedEmail, setDebouncedEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [selected, setSelected] = useState<UserHit | null>(null);
  const [dropdown, setDropdown] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const usersQ = useSearchUsersQuery(debouncedEmail);
  const chargesMut = useTransferChargesMutation();
  const transferMut = useTransferQndtMutation();

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedEmail(email.trim()), 400);
    return () => window.clearTimeout(t);
  }, [email]);

  useEffect(() => {
    const n = Number(amount);
    if (!selected || !n || n <= 0) return;
    const t = window.setTimeout(() => {
      chargesMut.mutate({ amount: n, userId: selected._id });
    }, 400);
    return () => window.clearTimeout(t);
  }, [amount, selected?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const users = useMemo(() => {
    const d = usersQ.data?.data;
    if (Array.isArray(d)) return d as UserHit[];
    if (Array.isArray(d?.users)) return d.users as UserHit[];
    if (d && typeof d === 'object' && (d as UserHit)._id) return [d as UserHit];
    return [];
  }, [usersQ.data]);

  if (!open) return null;

  const reset = () => {
    setEmail('');
    setDebouncedEmail('');
    setAmount('');
    setSelected(null);
    setDropdown(false);
    setSuccess(false);
    setError('');
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = () => {
    if (!selected) {
      setError('Select a recipient');
      return;
    }
    if (!selected.bankDetails) {
      setError('Recipient has no bank details');
      return;
    }
    const n = Number(amount);
    if (!n || n <= 0) {
      setError('Enter a valid amount');
      return;
    }
    if (n > Number(availableBalance || 0)) {
      setError('Insufficient balance');
      return;
    }
    transferMut.mutate(
      { userId: selected._id, reqAmount: n },
      { onSuccess: () => setSuccess(true) },
    );
  };

  const charges = chargesMut.data?.data;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={close} />
      <div className="relative z-10 w-full max-w-md overflow-hidden rounded-xl border border-[var(--dash-line)] bg-white shadow-xl">
        {success ? (
          <div className="px-6 py-10 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
              <Check className="h-7 w-7" />
            </div>
            <h2 className="text-lg font-bold text-[var(--dash-ink)]">
              Transfer successful
            </h2>
            <p className="mt-1 text-sm text-[var(--dash-muted)]">
              Transferred {formatMoney(amount)} QNDT
            </p>
            <button
              type="button"
              onClick={close}
              className="mt-6 rounded-lg px-8 py-2.5 text-sm font-semibold text-white"
              style={{ background: 'var(--dash-brand, #003481)' }}
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between border-b border-[var(--dash-line)] px-5 py-4">
              <div className="text-left">
                <h2 className="text-base font-bold text-[var(--dash-ink)]">
                  Transfer QNDT
                </h2>
                <p className="text-xs text-[var(--dash-muted)]">
                  Send tokens to another user
                </p>
              </div>
              <button
                type="button"
                onClick={close}
                className="rounded-md p-1 text-[var(--dash-muted)] hover:bg-[var(--dash-soft)]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 p-5 text-left">
              <div className="rounded-lg bg-[var(--dash-soft)] px-4 py-3">
                <p className="text-xs text-[var(--dash-muted)]">Available QNDT</p>
                <p className="mt-0.5 text-lg font-bold text-[var(--dash-ink)]">
                  {formatMoney(availableBalance)}
                </p>
              </div>

              <div className="relative">
                <label className="mb-1.5 block text-xs font-medium text-[var(--dash-muted)]">
                  Recipient email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setSelected(null);
                    setDropdown(true);
                    setError('');
                  }}
                  onFocus={() => setDropdown(true)}
                  placeholder="name@company.com"
                  className="w-full rounded-lg border border-[var(--dash-line)] px-3 py-2.5 text-sm outline-none focus:border-[var(--dash-brand)]"
                />
                {dropdown && debouncedEmail && !selected ? (
                  <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-[var(--dash-line)] bg-white shadow-lg">
                    {usersQ.isFetching ? (
                      <div className="flex justify-center py-4">
                        <Loader2 className="h-4 w-4 animate-spin text-[var(--dash-brand)]" />
                      </div>
                    ) : users.length === 0 ? (
                      <p className="px-3 py-4 text-center text-xs text-[var(--dash-muted)]">
                        No users found
                      </p>
                    ) : (
                      users.map((u) => (
                        <button
                          key={u._id}
                          type="button"
                          onClick={() => {
                            setSelected(u);
                            setEmail(u.email || '');
                            setDropdown(false);
                            setAmount('');
                            setError('');
                          }}
                          className="flex w-full items-center justify-between px-3 py-2.5 text-left hover:bg-[var(--dash-soft)]"
                        >
                          <div>
                            <p className="text-sm font-medium text-[var(--dash-ink)]">
                              {u.firstName} {u.lastName}
                            </p>
                            <p className="text-xs text-[var(--dash-muted)]">
                              {u.email}
                            </p>
                            {!u.bankDetails ? (
                              <p className="text-[10px] font-medium text-red-500">
                                No bank details
                              </p>
                            ) : null}
                          </div>
                          {u.bankDetails ? (
                            <Check className="h-4 w-4 text-emerald-500" />
                          ) : null}
                        </button>
                      ))
                    )}
                  </div>
                ) : null}
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-[var(--dash-muted)]">
                  Amount
                </label>
                <input
                  type="number"
                  value={amount}
                  disabled={!selected}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setError('');
                  }}
                  placeholder="Enter amount"
                  className="w-full rounded-lg border border-[var(--dash-line)] px-3 py-2.5 text-sm outline-none focus:border-[var(--dash-brand)] disabled:opacity-50"
                />
                {error ? (
                  <p className="mt-1 text-[11px] text-red-500">{error}</p>
                ) : null}
              </div>

              {charges && Number(amount) > 0 ? (
                <div className="rounded-lg border border-[var(--dash-line)] bg-[var(--dash-soft)]/50 px-3 py-2 text-xs text-[var(--dash-muted)]">
                  {charges.charges != null || charges.totalCharges != null ? (
                    <div className="flex justify-between py-0.5">
                      <span>Charges</span>
                      <span className="font-medium text-[var(--dash-ink)]">
                        {formatMoney(charges.charges ?? charges.totalCharges)}
                      </span>
                    </div>
                  ) : null}
                  {charges.netAmount != null ? (
                    <div className="flex justify-between py-0.5">
                      <span>Net amount</span>
                      <span className="font-medium text-[var(--dash-ink)]">
                        {formatMoney(charges.netAmount)}
                      </span>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="flex gap-2 border-t border-[var(--dash-line)] px-5 py-4">
              <button
                type="button"
                onClick={close}
                className="flex-1 rounded-lg border border-[var(--dash-line)] py-2.5 text-sm font-semibold text-[var(--dash-ink)] hover:bg-[var(--dash-soft)]"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={transferMut.isPending}
                onClick={submit}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: 'var(--dash-brand, #003481)' }}
              >
                {transferMut.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : null}
                Transfer
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
